import { sanitizeEducationalHtml } from "./educationalHtml";

const triggerCleanups = new WeakMap();
const openPopovers = new Map();
let globalListenersInstalled = false;
let nextPopoverId = 1;

function normalizeWidth(width) {
  if (!width || width === "auto") {
    return null;
  }
  return /^\d+$/.test(String(width)) ? `${width}px` : String(width);
}

function sourceFor(trigger) {
  const selector = trigger.dataset.content;
  if (!selector?.startsWith("#")) {
    return null;
  }
  return document.querySelector(selector);
}

function interactiveContent(source) {
  return source.querySelector("a, button, input, select, textarea, [tabindex]");
}

function sanitizedContent(source) {
  const template = document.createElement("template");
  template.innerHTML = sanitizeEducationalHtml(source.innerHTML);
  return template.content;
}

function choosePlacement(requested, triggerRect, popoverRect) {
  const placement = (requested || "auto").split("-")[0];
  if (placement !== "auto") {
    return placement;
  }

  const spaces = {
    bottom: window.innerHeight - triggerRect.bottom,
    left: triggerRect.left,
    right: window.innerWidth - triggerRect.right,
    top: triggerRect.top,
  };
  const required = {
    bottom: popoverRect.height,
    left: popoverRect.width,
    right: popoverRect.width,
    top: popoverRect.height,
  };

  return (
    ["bottom", "top", "right", "left"].find((candidate) => spaces[candidate] >= required[candidate] + 12) ||
    Object.keys(spaces).sort((a, b) => spaces[b] - spaces[a])[0]
  );
}

function positionPopover(trigger, popover, requestedPlacement) {
  const triggerRect = trigger.getBoundingClientRect();
  const popoverRect = popover.getBoundingClientRect();
  const placement = choosePlacement(requestedPlacement, triggerRect, popoverRect);
  const gap = 8;
  let left;
  let top;

  if (placement === "top" || placement === "bottom") {
    left = triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2;
    top = placement === "top" ? triggerRect.top - popoverRect.height - gap : triggerRect.bottom + gap;
  } else {
    left = placement === "left" ? triggerRect.left - popoverRect.width - gap : triggerRect.right + gap;
    top = triggerRect.top + triggerRect.height / 2 - popoverRect.height / 2;
  }

  left = Math.max(8, Math.min(left, window.innerWidth - popoverRect.width - 8));
  top = Math.max(8, Math.min(top, window.innerHeight - popoverRect.height - 8));

  popover.classList.remove("top", "right", "bottom", "left");
  popover.classList.add(placement);
  popover.style.left = `${left + window.scrollX}px`;
  popover.style.top = `${top + window.scrollY}px`;

  if (popover.querySelector("#SyncStatusPopoverContent")) {
    popover.style.top = `${window.scrollY}px`;
    const arrow = popover.querySelector(".webui-arrow");
    if (arrow) {
      arrow.style.top = "27px";
    }
  }
}

function closePopover(trigger) {
  const popover = openPopovers.get(trigger);
  if (!popover) {
    return;
  }
  popover.remove();
  openPopovers.delete(trigger);
  trigger.setAttribute("aria-expanded", "false");
  trigger.removeAttribute("aria-describedby");
}

function closeAllPopovers(exceptTrigger) {
  for (const trigger of openPopovers.keys()) {
    if (trigger !== exceptTrigger) {
      closePopover(trigger);
    }
  }
}

function openPopover(trigger, options) {
  const source = sourceFor(trigger);
  if (!source) {
    return;
  }

  closePopover(trigger);
  const id = `app-popover-${nextPopoverId++}`;
  const popover = document.createElement("div");
  popover.id = id;
  popover.className = "webui-popover app-popover in";
  const sanitized = sanitizedContent(source);
  const firstInteractiveElement = interactiveContent(sanitized);
  if (firstInteractiveElement) {
    popover.setAttribute("role", "dialog");
    popover.setAttribute("aria-modal", "false");
    popover.setAttribute("aria-label", trigger.getAttribute("aria-label") || "More information");
  } else {
    popover.setAttribute("role", "tooltip");
  }

  const arrow = document.createElement("div");
  arrow.className = "webui-arrow";
  const inner = document.createElement("div");
  inner.className = "webui-popover-inner";
  const content = document.createElement("div");
  content.className = "webui-popover-content";

  content.append(sanitized);

  inner.append(content);
  popover.append(arrow, inner);
  const width = normalizeWidth(trigger.dataset.width || options.width);
  if (width) {
    popover.style.width = width;
  }
  document.body.append(popover);
  positionPopover(trigger, popover, trigger.dataset.placement || options.placement);

  openPopovers.set(trigger, popover);
  trigger.setAttribute("aria-expanded", "true");
  trigger.setAttribute("aria-describedby", id);
  if (firstInteractiveElement) {
    popover.querySelector("a, button, input, select, textarea, [tabindex]")?.focus();
  }
}

function togglePopover(event, trigger, options) {
  event.preventDefault();
  event.stopPropagation();
  if (openPopovers.has(trigger)) {
    closePopover(trigger);
  } else {
    openPopover(trigger, options);
  }
}

function initializeTrigger(trigger, options) {
  const existingCleanup = triggerCleanups.get(trigger);
  if (existingCleanup) {
    return existingCleanup;
  }
  const source = sourceFor(trigger);
  if (source && interactiveContent(sanitizedContent(source))) {
    trigger.setAttribute("aria-haspopup", "dialog");
  }
  if (!trigger.getAttribute("aria-label") && !trigger.textContent.trim()) {
    trigger.setAttribute("aria-label", "More information");
  }
  if (!trigger.matches("a, button, input, select, textarea, [tabindex]")) {
    trigger.setAttribute("role", "button");
    trigger.tabIndex = 0;
  }
  trigger.setAttribute("aria-expanded", "false");

  const handlers = [];
  const addHandler = (eventName, handler) => {
    trigger.addEventListener(eventName, handler);
    handlers.push([eventName, handler]);
  };
  const configuredTrigger = trigger.dataset.trigger || options.trigger || "click";
  if (configuredTrigger === "hover") {
    addHandler("mouseenter", () => openPopover(trigger, options));
    addHandler("mouseleave", () => closePopover(trigger));
    addHandler("focus", () => openPopover(trigger, options));
    addHandler("blur", () => closePopover(trigger));
    addHandler("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!openPopovers.has(trigger)) {
        openPopover(trigger, options);
      }
    });
  } else {
    addHandler("click", (event) => togglePopover(event, trigger, options));
    addHandler("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        togglePopover(event, trigger, options);
      }
    });
  }

  const cleanup = () => {
    closePopover(trigger);
    for (const [eventName, handler] of handlers) {
      trigger.removeEventListener(eventName, handler);
    }
    triggerCleanups.delete(trigger);
  };
  triggerCleanups.set(trigger, cleanup);
  return cleanup;
}

function installGlobalListeners() {
  if (globalListenersInstalled) {
    return;
  }
  globalListenersInstalled = true;
  document.addEventListener("click", (event) => {
    const activeTrigger = event.target.closest?.('[data-app-popover="true"]');
    const activePopover = event.target.closest?.(".app-popover");
    if (!activeTrigger && !activePopover) {
      closeAllPopovers();
    }
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      const triggers = [...openPopovers.keys()];
      closeAllPopovers();
      triggers.at(-1)?.focus();
    }
  });
  window.addEventListener("resize", () => {
    for (const [trigger, popover] of openPopovers) {
      if (!trigger.isConnected) {
        closePopover(trigger);
      } else {
        positionPopover(trigger, popover, trigger.dataset.placement);
      }
    }
  });
}

export function initializePopovers(options = {}) {
  installGlobalListeners();
  document.querySelectorAll('[data-app-popover="true"]').forEach((trigger) => {
    initializeTrigger(trigger, options);
  });
}

export function initializePopoverTrigger(trigger, options = {}) {
  installGlobalListeners();
  return initializeTrigger(trigger, options);
}

export function destroyPopovers() {
  closeAllPopovers();
}
